import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { KmTableroInput } from '../KmTablero';
import DocumentoDialog, { conVistaPrevia, useVistaPdf } from './DocumentoDialog';
import EnviarDocumento, { Canal, Envio } from './EnviarDocumento';
import { formatKm, parseKm } from '../../utils/km';

interface Informe {
  otId: string;
  conImportes: boolean;
  ot: { codigo: string; km: number | null; ingreso: string; egreso: string };
  cliente: { id: string | null; nombre: string; contacto: string; email: string; telefono: string };
  unidad: { id: string | null; patente: string; interno: string; descripcion: string; kmFicha: number | null };
}

/**
 * Informe de servicio de una OT: lo que se le hizo a la unidad a los km del
 * ingreso. El diagnóstico (checklist) es otro documento y no va acá.
 */
const InformeServicioDialog: React.FC<{
  appointmentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ appointmentId, open, onOpenChange }) => {
  const [informe, setInforme] = useState<Informe | null>(null);
  const [envios, setEnvios] = useState<Envio[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [conImportes, setConImportes] = useState(false);
  const [km, setKm] = useState('');
  const [editandoKm, setEditandoKm] = useState(false);
  const [guardandoKm, setGuardandoKm] = useState(false);
  const vista = useVistaPdf();
  const { generar, limpiar } = vista;

  const pedirPdf = useCallback((conImp: boolean) => () =>
    axios.post(`/api/informes/ot/${appointmentId}/pdf`, { conImportes: conImp }, { responseType: 'blob' }).then((r) => r.data as Blob),
  [appointmentId]);

  const refrescarVista = useCallback((conImp: boolean) => {
    if (conVistaPrevia()) generar(pedirPdf(conImp));
  }, [generar, pedirPdf]);

  useEffect(() => {
    if (!open || !appointmentId) { setInforme(null); limpiar(); return; }
    let vivo = true;
    setError(null);
    axios.get(`/api/informes/ot/${appointmentId}`)
      .then(({ data }) => {
        if (!vivo) return;
        const inf: Informe = data.informe;
        setInforme(inf);
        setEnvios(data.envios || []);
        setConImportes(!!inf.conImportes);
        setKm('');
        setEditandoKm(!inf.ot.km && !!inf.unidad.id);
        refrescarVista(!!inf.conImportes);
      })
      .catch((e) => { if (vivo) setError(e.response?.data?.error || 'No se pudo cargar el informe'); });
    return () => { vivo = false; };
  }, [open, appointmentId, limpiar, refrescarVista]);

  const faltaKm = !!informe?.unidad.id && !informe?.ot.km;

  const cambiarImportes = async (v: boolean) => {
    setConImportes(v);
    refrescarVista(v);
    axios.put(`/api/informes/ot/${appointmentId}`, { conImportes: v }).catch(() => {});
  };

  const guardarKm = async () => {
    if (!informe || parseKm(km) == null) return;
    setGuardandoKm(true);
    try {
      const { data } = await axios.put(`/api/informes/ot/${informe.otId}`, { km: parseKm(km), conImportes });
      setInforme(data.informe);
      setKm('');
      setEditandoKm(false);
      refrescarVista(conImportes);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudieron guardar los km');
    } finally {
      setGuardandoKm(false);
    }
  };

  const enviar = async (canal: Canal, destino: string, guardarMail: boolean) => {
    if (!informe) return;
    try {
      const { data } = await axios.post(`/api/informes/ot/${informe.otId}/enviar`, { canal, destino, guardarMail, conImportes });
      setEnvios(data.envios || []);
      toast.success(canal === 'mail' ? `Informe enviado a ${destino}` : 'Informe enviado por WhatsApp');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo enviar el informe');
    }
  };

  const archivo = informe ? `Informe_${informe.ot.codigo}${informe.unidad.patente ? `_${informe.unidad.patente}` : ''}.pdf` : 'Informe.pdf';
  const subtitulo = informe
    ? <>{[informe.ot.codigo, informe.unidad.patente, informe.unidad.descripcion].filter(Boolean).join(' · ')}<br />{informe.cliente.nombre}</>
    : null;

  return (
    <DocumentoDialog
      open={open}
      onOpenChange={onOpenChange}
      titulo="Informe de servicio"
      subtitulo={subtitulo}
      vista={vista}
      archivo={archivo}
      pedirPdf={pedirPdf(conImportes)}
      puedeAbrir={!!informe}
      pie={informe && (
        <EnviarDocumento
          mailInicial={informe.cliente.email}
          telefonoInicial={informe.cliente.telefono}
          ofrecerGuardarMail={!informe.cliente.email}
          bloqueo={faltaKm ? 'Cargá los km antes de enviar.' : null}
          envios={envios}
          onEnviar={enviar}
        />
      )}
    >
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !informe ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <>
          <section>
            <div className="text-xs font-medium text-muted-foreground">Km al ingreso</div>
            {editandoKm ? (
              <div className="mt-1.5 space-y-2">
                <KmTableroInput id="informe-km" value={km} onChange={setKm} kmFicha={informe.unidad.kmFicha} autoFocus />
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={guardarKm} disabled={guardandoKm || parseKm(km) == null}>
                    {guardandoKm ? 'Guardando…' : 'Guardar km'}
                  </Button>
                  {!!informe.ot.km && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setEditandoKm(false); setKm(''); }}>Cancelar</Button>
                  )}
                </div>
                {faltaKm && <p className="text-xs text-muted-foreground">Esta OT no tiene los km cargados.</p>}
              </div>
            ) : (
              <div className="mt-0.5 flex items-baseline justify-between gap-2">
                <span className="text-2xl font-semibold tabular-nums">{informe.ot.km ? formatKm(informe.ot.km) : 'sin dato'}</span>
                {informe.unidad.id && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditandoKm(true)}>
                    <PencilIcon className="mr-1 h-3.5 w-3.5" />Corregir
                  </Button>
                )}
              </div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Ingreso {informe.ot.ingreso}{informe.ot.egreso ? ` · Entrega ${informe.ot.egreso}` : ''}
            </p>
          </section>

          <label className="flex cursor-pointer items-center justify-between gap-3">
            <span>
              <span className="block text-sm font-medium">Incluir importes</span>
              <span className="block text-xs text-muted-foreground">Precio de cada trabajo y total</span>
            </span>
            <Switch checked={conImportes} onCheckedChange={cambiarImportes} />
          </label>
        </>
      )}
    </DocumentoDialog>
  );
};

export default InformeServicioDialog;
